// Generic chat UI helpers
import { normalizeSlug } from '@/lib/seo/slug';

export function avatarUrl(loc?: string | null): string {
  const CDN = (process.env.NEXT_PUBLIC_AVATAR_CDN || "https://d14nsfeg9ivjwi.cloudfront.net").replace(/\/$/, "");
  if (!loc || !loc.trim()) return "/img/default-avatar.svg";
  if (/^https?:\/\//i.test(loc)) return loc;
  return `${CDN}/${loc.replace(/^\/+/, "")}`;
}

export function toSlug(s?: string | null): string {
  if (!s) return "";
  return s
    .trim()
    .toLowerCase()
    .replace(/['’"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function formatThreadTime(iso?: string | null): string {
  if (!iso) return "";
  // Ensure ISO string is properly formatted (add Z if missing to indicate UTC)
  let isoStr = String(iso).trim();
  if (isoStr && !isoStr.endsWith('Z') && !isoStr.includes('+') && !isoStr.includes('-', 10)) {
    isoStr = isoStr + 'Z';
  }
  const d = new Date(isoStr);
  const now = new Date();

  // Compare using UTC dates to avoid timezone issues
  const dUTC = new Date(d.toISOString());
  const nowUTC = new Date(now.toISOString());
  const sameDay =
    dUTC.getUTCFullYear() === nowUTC.getUTCFullYear() &&
    dUTC.getUTCMonth() === nowUTC.getUTCMonth() &&
    dUTC.getUTCDate() === nowUTC.getUTCDate();
  const ms = now.getTime() - d.getTime();
  const days = ms / 86_400_000;

  if (sameDay) {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  if (days < 7) {
    return d.toLocaleDateString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Build a public profile link.
 * Prefers canonical /u/{slug} when a stageName is available; falls back to /details/{id}.
 */
export function buildProfileLink(otherUserId: string, stageName?: string | null): string {
  if (stageName) {
    const slug = normalizeSlug(stageName);
    if (slug.length >= 3) return `/u/${slug}`;
  }
  const id = (otherUserId || '').trim();
  return `/details/${encodeURIComponent(id)}`;
}

/** Returns true if the browser indicates data-saver or slow connection (2g/3g). */
export function detectDataSaver(): boolean {
  try {
    const nav: any = typeof navigator !== 'undefined' ? (navigator as any) : null;
    const conn: any = nav?.connection || nav?.mozConnection || nav?.webkitConnection;
    if (!conn) return false;
    if (conn.saveData) return true;
    const et: string | undefined = conn.effectiveType;
    if (!et) return false;
    const t = String(et).toLowerCase();
    return t === 'slow-2g' || t === '2g' || t === '3g';
  } catch {
    return false;
  }
}

export function formatLastSeen(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.max(0, now.getTime() - d.getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/** Downscale & encode image as data URL to keep payloads small; fallback when upload endpoint is unavailable. */
export async function compressImageToDataUrl(
  file: File,
  maxDim = 1600,
  mime = "image/jpeg",
  quality = 0.8
): Promise<string> {
  const img = await fileToImage(file);
  const { width, height } = fitContain(img.width, img.height, maxDim);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL(mime, quality);
}

/**
 * Compress an image to roughly targetKb using WebP when supported; JPEG fallback.
 * Returns { dataUrl, mime }. Quality controls average size; a dynamic cap applies in Data Saver.
 */
export async function compressToWebpOrJpeg(
  file: File,
  maxPx = 1600,
  targetKb = 180,
  quality = 0.8
): Promise<{ dataUrl: string; mime: string }> {
  const img = await fileToImage(file);
  const { width, height } = fitContain(img.width, img.height, maxPx);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unsupported");
  ctx.drawImage(img, 0, 0, width, height);

  // Helper to try a mime/quality and return bytes
  const tryEncode = (mime: string, q: number) => {
    const url = canvas.toDataURL(mime, q);
    const bytes = Math.ceil((url.length * 3) / 4) - (url.endsWith("==") ? 2 : url.endsWith("=") ? 1 : 0);
    return { url, bytes, mime };
  };

  const target = targetKb * 1024;
  const saver = inDataSaver();
  const cap = (saver ? 200 : 300) * 1024; // tighter cap in data saver

  // Binary search quality to approach target size
  async function fit(mime: string): Promise<{ url: string; bytes: number; mime: string }> {
    let lo = 0.4, hi = Math.min(0.95, Math.max(quality, 0.5)), best = tryEncode(mime, hi);
    for (let i = 0; i < 8; i++) {
      const mid = (lo + hi) / 2;
      const cand = tryEncode(mime, mid);
      best = cand.bytes <= target ? cand : best;
      if (cand.bytes > target) {
        hi = mid;
      } else {
        lo = mid;
      }
    }
    // One last check near lo for slightly smaller size
    const final = best.bytes > target ? tryEncode(mime, lo) : best;
    return final;
  }

  // Try WebP first when supported by canvas
  let result: { url: string; bytes: number; mime: string } | null = null;
  try {
    const test = canvas.toDataURL("image/webp", 0.8);
    if (/^data:image\/webp/.test(test)) {
      result = await fit("image/webp");
    }
  } catch {}
  if (!result || result.bytes > cap) {
    // Fallback to JPEG
    const jpeg = await fit("image/jpeg");
    result = jpeg;
  }
  if (result.bytes > cap) {
    const kb = Math.round(result.bytes / 1024);
    throw new Error(`Image too large after compression (~${kb}KB). Try a smaller image.`);
  }
  return { dataUrl: result.url, mime: result.mime };
}

function fileToImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = reject;
    img.src = url;
  });
}

function fitContain(w: number, h: number, max: number) {
  if (Math.max(w, h) <= max) return { width: w, height: h };
  const ratio = w >= h ? max / w : max / h;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

// ------- Data Saver & Preview helpers -------

const DS_KEY = "chat_data_saver";

export function inDataSaver(next?: boolean): boolean {
  if (typeof window === "undefined") return false;
  if (typeof next === "boolean") {
    try { sessionStorage.setItem(DS_KEY, next ? "1" : "0"); } catch {}
    return next;
  }
  try { return sessionStorage.getItem(DS_KEY) === "1"; } catch { return false; }
}

export function shouldShowPreview(url?: string | null): boolean {
  if (!url) return false;
  if (!/^https?:\/\//i.test(url)) return false;
  return true;
}
